"use client"

import { useState, type ComponentType, type ComponentProps } from "react"
import { Form, Formik } from "formik"
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  Plus,
  RotateCcw,
  Sparkles,
  Trash2,
} from "lucide-react"

import { FormTextarea } from "@/components/form-inputs"
import { VoiceLanguageSelector } from "@/components/agents/voice/voice-language-selector"
import {
  AgentNameField,
  AgentPromptPreparation,
  type SignupOnboardingPromptApproval,
  validateAgentName,
} from "@/components/onboarding/agent-name-personalization"
import { BusinessFactsPromptConfirmationDialog } from "@/components/onboarding/business-facts-prompt-confirmation-dialog"
import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import type { SignupOnboardingPersonalizationSource } from "@/lib/onboarding/personalization-source"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  formatSignupOnboardingBusinessSummary,
  parseSignupOnboardingBusinessSummary,
} from "@/lib/onboarding/signup-onboarding"
import { ONBOARDING_LANGUAGE_OPTIONS } from "@/lib/onboarding/languages"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






export function PromptReviewForm({
  Actions = OnboardingStepActions,
  agentName,
  agentNameEditable = true,
  showAgentName = true,
  knowledgeMode = "selected",
  originalPrompt,
  enrichedPrompt,
  businessSummary,
  languages,
  personalizationWaiting,
  personalizationSource = "website",
  pagesReady,
  websitePagesTotal,
  personalizationError,
  onRetryPersonalization,
  onRetryKnowledgeImport,
  onRegenerate,
  onApprove,
  regenerating,
  submitting,
  submitLabel,
  submittingLabel,
}: {
  Actions?: ComponentType<ComponentProps<typeof OnboardingStepActions>>
  agentName: string
  agentNameEditable?: boolean
  showAgentName?: boolean
  knowledgeMode?: "selected" | "skipped"
  originalPrompt: string
  enrichedPrompt: string | null
  businessSummary: string | null
  languages: string[]
  personalizationWaiting: boolean
  personalizationSource?: SignupOnboardingPersonalizationSource
  pagesReady: number
  websitePagesTotal?: number
  personalizationError: string | null
  onRetryPersonalization: () => void
  onRetryKnowledgeImport?: () => void
  onRegenerate: (input: {
    businessFacts: string[]
    languages: string[]
  }) => Promise<void>
  onApprove: (input: SignupOnboardingPromptApproval) => Promise<void>
  regenerating: boolean
  submitting: boolean
  submitLabel?: string
  submittingLabel?: string
}) {



  const tUi = useTranslations("Onboarding.PromptReviewForm")

  const hasEnrichedPrompt = Boolean(enrichedPrompt)
  const [showAllBusinessFacts, setShowAllBusinessFacts] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [pendingApproval, setPendingApproval] =
    useState<SignupOnboardingPromptApproval | null>(null)
  const initialBusinessSummary =
    formatSignupOnboardingBusinessSummary(
      parseSignupOnboardingBusinessSummary(businessSummary),
    ) || null

  if (personalizationWaiting && !hasEnrichedPrompt) {
    return (
      <AgentPromptPreparation
        agentName={agentName}
        originalPrompt={originalPrompt}
        languages={languages}
        pagesReady={pagesReady}
        websitePagesTotal={websitePagesTotal}
        personalizationSource={personalizationSource}
        submitting={submitting}
        onApprove={onApprove}
      />
    )
  }

  return (
    <Formik
      initialValues={{
        agentName,
        choice: hasEnrichedPrompt ? ("enriched" as const) : ("original" as const),
        prompt: enrichedPrompt ?? originalPrompt,
        summaryItems: parseSignupOnboardingBusinessSummary(businessSummary),
        languages,
      }}
      enableReinitialize
      validate={validatePromptReview}
      onSubmit={async (values, helpers) => {
        const approval = {
          agentName: values.agentName.trim(),
          prompt: values.prompt.trim(),
          summary:
            formatSignupOnboardingBusinessSummary(values.summaryItems) || null,
          languages: values.languages,
          choice: values.choice,
        }
        if (
          approval.summary !== initialBusinessSummary ||
          !areLanguagesEqual(approval.languages, languages)
        ) {
          setPendingApproval(approval)
          setConfirmationOpen(true)
          helpers.setSubmitting(false)
          return
        }
        try {
          await onApprove(approval)
        } catch (error) {
          helpers.setStatus(
            error instanceof Error ? error.message : "Could not create the agent.",
          )
        }
      }}
    >
      {({
        errors,
        status,
        touched,
        values,
        setFieldTouched,
        setFieldValue,
        setStatus,
      }) => {
        const formattedBusinessSummary =
          formatSignupOnboardingBusinessSummary(values.summaryItems) || null
        const businessFactsChanged =
          formattedBusinessSummary !== initialBusinessSummary
        const languagesChanged = !areLanguagesEqual(values.languages, languages)
        const personalizationDetailsChanged =
          businessFactsChanged || languagesChanged
        const regeneratePrompt = async () => {
          try {
            await onRegenerate({
              businessFacts: parseSignupOnboardingBusinessSummary(
                formattedBusinessSummary,
              ),
              languages: values.languages,
            })
            setPendingApproval(null)
            setConfirmationOpen(false)
          } catch (error) {
            setStatus(
              error instanceof Error
                ? error.message
                : "Could not re-generate the agent prompt.",
            )
          }
        }

        return (
          <>
            <Form className="space-y-5">
              {showAgentName ? <AgentNameField editable={agentNameEditable} /> : null}

              {personalizationError && !hasEnrichedPrompt ? (
                <Alert className="border-amber-300 bg-amber-50">
              <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                <span>{personalizationError}</span>
                <span className="flex flex-wrap gap-2">
                  {onRetryKnowledgeImport ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={onRetryKnowledgeImport}
                    >
                      <span key={tUi("retryKnowledgeImport")}>{tUi("retryKnowledgeImport")}</span></Button>
                  ) : null}
                  <Button type="button" size="sm" variant="outline" onClick={onRetryPersonalization}>
                    <span key={tUi("retryPersonalization")}>{tUi("retryPersonalization")}</span></Button>
                </span>
              </AlertDescription>
            </Alert>
          ) : null}

          {hasEnrichedPrompt && knowledgeMode === "selected" ? (
            <Card className="border-blue-200 bg-blue-50/40">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                  <span key={tUi("whatWeFoundAboutYourBusiness")}>{tUi("whatWeFoundAboutYourBusiness")}</span></CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <VoiceLanguageSelector
                  options={ONBOARDING_LANGUAGE_OPTIONS}
                  value={values.languages}
                  onChange={(nextLanguages) => {
                    void setFieldValue("languages", nextLanguages)
                    void setFieldTouched("languages", true, false)
                  }}
                />
                {touched.languages && errors.languages ? (
                  <p className="text-sm text-destructive">
                    {errors.languages}
                  </p>
                ) : null}
                <fieldset>
                  <legend className="sr-only"><span key={tUi("businessInformationFound")}>{tUi("businessInformationFound")}</span></legend>
                  <ul className="space-y-2">
                    {values.summaryItems
                      .slice(0, showAllBusinessFacts ? undefined : 5)
                      .map((item, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2"
                        >
                          <span
                            className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600"
                            aria-hidden="true"
                          />
                          <Input
                            aria-label={tUi("businessFactValue0", { value0: String(index + 1) })}
                            value={item}
                            onChange={(event) =>
                              void setFieldValue(
                                `summaryItems.${index}`,
                                event.target.value,
                              )
                            }
                            className="bg-background"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={tUi("removeBusinessFactValue0", { value0: String(index + 1) })}
                            onClick={() =>
                              void setFieldValue(
                                "summaryItems",
                                values.summaryItems.filter(
                                  (_, itemIndex) => itemIndex !== index,
                                ),
                              )
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                  </ul>
                </fieldset>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                    onClick={() => {
                      setShowAllBusinessFacts(true)
                      void setFieldValue("summaryItems", [
                        ...values.summaryItems,
                        "",
                      ])
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span key={tUi("addBusinessFact")}>{tUi("addBusinessFact")}</span></Button>
                  {values.summaryItems.length > 5 ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="min-w-28 whitespace-nowrap"
                      onClick={() =>
                        setShowAllBusinessFacts((current) => !current)
                      }
                    >
                      {showAllBusinessFacts ? (
                        <ChevronUp className="mr-2 h-4 w-4" />
                      ) : (
                        <ChevronDown className="mr-2 h-4 w-4" />
                      )}
                      <span key={showAllBusinessFacts ? tUi("hide") : tUi("showAll")}>{showAllBusinessFacts ? tUi("hide") : tUi("showAll")}</span>
                    </Button>
                  ) : null}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  <span key={tUi("correctRemoveOrAddFactsBeforeApprovingThe")}>{tUi("correctRemoveOrAddFactsBeforeApprovingThe")}</span></p>
              </CardContent>
            </Card>
          ) : null}

          {personalizationDetailsChanged ? (
            <Alert className="border-blue-300 bg-blue-50">
              <Sparkles className="h-4 w-4 text-blue-700" />
              <AlertDescription className="flex flex-col gap-3 text-blue-950 sm:flex-row sm:items-center sm:justify-between">
                <span><TranslationText>{tUi.rich("tag0Tag1PersonalizationDetailsChangedTag1Tag0Re", { tag0: chunks => (<strong className="block">{chunks}</strong>), tag1: chunks => (<span>{chunks}</span>) })}</TranslationText></span>
                <Button
                  type="button"
                  className="shrink-0"
                  onClick={() => void regeneratePrompt()}
                  disabled={regenerating || submitting}
                >
                  {regenerating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  <span key={regenerating ? tUi("reGenerating") : tUi("reGeneratePrompt")}>{regenerating ? tUi("reGenerating") : tUi("reGeneratePrompt")}</span>
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label htmlFor="signup-agent-prompt" className="text-base font-medium">
                <span key={tUi("completeAgentPrompt")}>{tUi("completeAgentPrompt")}</span></Label>
              {hasEnrichedPrompt ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const nextChoice = values.choice === "enriched" ? "original" : "enriched"
                    void setFieldValue("choice", nextChoice)
                    void setFieldValue(
                      "prompt",
                      nextChoice === "original" ? originalPrompt : enrichedPrompt,
                    )
                  }}
                >
                  {values.choice === "enriched" ? (
                    <>
                      <FileText className="mr-2 h-4 w-4" />
                      <span key={tUi("viewOriginalRolePrompt")}>{tUi("viewOriginalRolePrompt")}</span></>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      <span key={tUi("viewEnrichedPrompt")}>{tUi("viewEnrichedPrompt")}</span></>
                  )}
                </Button>
              ) : null}
            </div>
            <FormTextarea
              id="signup-agent-prompt"
              name="prompt"
              label={tUi("agentInstructions")}
              rows={18}
              className="font-mono text-sm leading-relaxed"
            />
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
              <span key={values.choice === "enriched"
                  ? tUi("websiteEnrichedAgentInstruction")
                  : tUi("originalInstructionFromTheSelectedRoles")}>
                {values.choice === "enriched"
                  ? tUi("websiteEnrichedAgentInstruction")
                  : tUi("originalInstructionFromTheSelectedRoles")}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  void setFieldValue(
                    "prompt",
                    values.choice === "enriched" ? enrichedPrompt : originalPrompt,
                  )
                }
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                <span key={tUi("restoreThisVersion")}>{tUi("restoreThisVersion")}</span></Button>
            </div>
          </div>

          {status ? <p className="text-sm text-destructive">{status}</p> : null}

          <Actions>
            <Button type="submit" disabled={submitting || regenerating}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              <span>{submitting
                ? submittingLabel ?? tUi("creatingAgent")
                : submitLabel ?? tUi("approveAndCreateAgent")}</span>
            </Button>
          </Actions>
            </Form>
            <BusinessFactsPromptConfirmationDialog
              open={confirmationOpen}
              onOpenChange={(open) => {
                setConfirmationOpen(open)
                if (!open) setPendingApproval(null)
              }}
              onContinue={() => {
                const approval = pendingApproval
                setConfirmationOpen(false)
                setPendingApproval(null)
                if (!approval) return
                void onApprove(approval).catch((error: unknown) => {
                  setStatus(
                    error instanceof Error
                      ? error.message
                      : "Could not create the agent.",
                  )
                })
              }}
              onRegenerate={() => void regeneratePrompt()}
              regenerating={regenerating}
            />
          </>
        )
      }}
    </Formik>
  )
}

function areLanguagesEqual(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function validatePromptReview({
  agentName,
  languages,
  prompt,
}: {
  agentName: string
  languages: string[]
  prompt: string
}) {
  const errors: { agentName?: string; languages?: string; prompt?: string } = {}
  const agentNameError = validateAgentName(agentName).agentName
  if (agentNameError) errors.agentName = agentNameError
  if (languages.length === 0) {
    errors.languages = "Choose at least one language."
  }
  if (!prompt.trim()) {
    errors.prompt = "The agent prompt cannot be empty."
  }
  return errors
}
